import {
  Component,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  output,
  input
} from '@angular/core';
import { Params, RouterLink } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';

export type SbButtonVariant =
  | 'primary'
  | 'default'
  | 'secondary'
  | 'danger'
  | 'success'
  | 'outline'
  | 'outline-secondary'
  | 'outline-danger'
  | 'ghost'
  | 'link';

export type SbButtonSize = 'xs' | 'sm' | 'md' | 'lg';
export type SbButtonShape = 'default' | 'pill' | 'circle';
export type SbButtonType = 'button' | 'submit' | 'reset';
export type SbButtonIconPosition = 'start' | 'end';

const VARIANT_CLASS: Record<SbButtonVariant, string> = {
  primary: 'btn-primary',
  default: 'btn-default',
  secondary: 'btn-secondary',
  danger: 'btn-danger',
  success: 'btn-success',
  outline: 'btn-outline-primary',
  'outline-secondary': 'btn-outline-secondary',
  'outline-danger': 'btn-outline-danger',
  ghost: 'btn-icon',
  link: 'btn-link',
};

const SIZE_CLASS: Record<SbButtonSize, string> = {
  xs: 'btn-xs',
  sm: 'btn-sm',
  md: '',
  lg: 'btn-lg',
};

@Component({
  selector: 'app-sb-button',
  templateUrl: './sb-button.component.html',
  styleUrls: ['./sb-button.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, RouterLink, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu]
})
export class SbButtonComponent {
  readonly variant = input<SbButtonVariant>('primary');
  readonly size = input<SbButtonSize>('md');
  readonly shape = input<SbButtonShape>('default');
  readonly type = input<SbButtonType>('button');
  readonly disabled = input(false);
  readonly loading = input(false);
  readonly active = input(false);
  readonly icon = input('');
  readonly iconPosition = input<SbButtonIconPosition>('start');
  readonly iconOnly = input(false);
  readonly ariaLabel = input('');
  readonly label = input('');
  readonly block = input(false);
  readonly dropdown = input(false);
  readonly href = input<string | null>(null);
  readonly routerLink = input<string | readonly unknown[] | null>(null);
  readonly queryParams = input<Params | null>(null);
  readonly fragment = input<string | null>(null);
  readonly target = input<string | null>(null);
  readonly rel = input<string | null>(null);
  readonly extraClass = input('');

  readonly clicked = output<MouseEvent>();

  get isLink(): boolean {
    return this.href() != null || this.routerLink() != null;
  }

  get isDisabled(): boolean {
    return this.disabled() || this.loading();
  }

  get hostClasses(): string {
    const parts = ['btn', 'sb-button', VARIANT_CLASS[this.variant()] || VARIANT_CLASS.primary];
    const sizeClass = SIZE_CLASS[this.size()];
    if (sizeClass) {
      parts.push(sizeClass);
    }
    const shape = this.shape();
    if (shape === 'pill') {
      parts.push('btn-pill');
    }
    if (shape === 'circle' || this.iconOnly()) {
      parts.push('sb-button--icon-only');
    }
    if (shape === 'circle') {
      parts.push('rounded-circle', 'sb-button--circle');
    }
    if (this.block()) {
      parts.push('w-100');
    }
    if (this.dropdown()) {
      parts.push('dropdown-toggle');
    }
    if (this.active()) {
      parts.push('active');
    }
    if (this.loading()) {
      parts.push('sb-button--loading');
    }
    const extraClass = this.extraClass();
    if (extraClass) {
      parts.push(extraClass);
    }
    return parts.filter(Boolean).join(' ');
  }

  get resolvedAriaLabel(): string | null {
    const ariaLabel = this.ariaLabel();
    if (ariaLabel) {
      return ariaLabel;
    }
    const label = this.label();
    if (this.iconOnly() && label) {
      return label;
    }
    return null;
  }

  get showIconStart(): boolean {
    return !!this.icon() && this.iconPosition() === 'start' && !this.loading();
  }

  get showIconEnd(): boolean {
    return !!this.icon() && this.iconPosition() === 'end' && !this.loading();
  }

  get showLabel(): boolean {
    return !!this.label() && !this.iconOnly();
  }

  onClick(event: MouseEvent): void {
    if (this.isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.clicked.emit(event);
  }
}
